import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import aiReplacesHero from "@/assets/editorial/ai-replaces-marketing-teams.jpeg";
import campaignMonitoringHero from "@/assets/editorial/campaign-monitoring-hero.jpeg";
import dataSilosHero from "@/assets/editorial/data-silos-hero.jpeg";
import scalingHero from "@/assets/editorial/scaling-without-headcount-hero.jpeg";

const EditorialSection = () => {
  const featuredArticle = {
    slug: "scaling-without-headcount",
    image: scalingHero,
    category: "Business Strategy",
    readTime: "18 min read",
    title: "Scaling Revenue Without Scaling Headcount: The 2026 Playbook",
    description: "In 2026, the winning strategy is scaling systems, not headcount. This definitive playbook shows how to achieve 2x, 5x, or 10x revenue growth while maintaining lean teams through strategic automation.",
  };

  const articles = [
    {
      slug: "ai-ecommerce-future",
      image: aiReplacesHero,
      category: "AI Marketing",
      readTime: "15 min read",
      title: "Why 2026 is the Year AI Replaces Marketing Teams",
      description: "Marketing teams are being replaced by AI automation in 2026, but marketers are thriving...",
    },
    {
      slug: "ai-strategies-dtc-brand",
      image: campaignMonitoringHero,
      category: "Campaign Optimization",
      readTime: "12 min read",
      title: "Stop Losing Money to Manual Campaign Monitoring",
      description: "Manual campaign monitoring creates gaps—hours or days where performance problems compound silently...",
    },
    {
      slug: "machine-learning-recommendations",
      image: dataSilosHero,
      category: "Marketing Operations",
      readTime: "14 min read",
      title: "The Hidden Tax of Data Silos",
      description: "Your platforms claim 73 conversions but you only made 47 sales. This is the data silo tax—learn the real cost...",
    },
  ];

  return (
    <section className="py-16 md:py-24 px-6">
      <div className="mx-auto max-w-7xl">
        {/* Featured Article */}
        <Link 
          to={`/editorial/${featuredArticle.slug}`}
          className="group block mb-16"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 bg-background border border-border rounded-2xl overflow-hidden">
            <div className="aspect-[4/3] lg:aspect-auto overflow-hidden">
              <img 
                src={featuredArticle.image} 
                alt={featuredArticle.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="flex flex-col justify-center p-6 lg:p-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                  {featuredArticle.category}
                </span>
                <span className="text-sm text-muted-foreground">{featuredArticle.readTime}</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold text-foreground leading-tight mb-4 group-hover:text-blue-600 transition-colors">
                {featuredArticle.title}
              </h3>
              <p className="text-muted-foreground mb-6 line-clamp-3">
                {featuredArticle.description}
              </p>
              <div className="flex items-center gap-2 text-blue-600 font-medium group-hover:gap-3 transition-all">
                <span>Read Article</span>
                <ArrowRight size={18} />
              </div>
            </div>
          </div>
        </Link>

        {/* Latest Articles Header */}
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl md:text-3xl font-normal text-foreground">
            Latest Articles
          </h2>
          <Link 
            to="/editorial"
            className="flex items-center gap-2 text-blue-600 font-medium hover:gap-3 transition-all"
          >
            <span>View All Articles</span>
            <ArrowRight size={18} />
          </Link>
        </div>
        
        {/* Article Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map((article) => (
            <Link 
              key={article.slug} 
              to={`/editorial/${article.slug}`}
              className="group block bg-background border border-border rounded-xl overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src={article.image} 
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5 space-y-3">
                <div className="flex items-center gap-3">
                  <span className="text-xs px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full">
                    {article.category}
                  </span>
                  <span className="text-xs text-gray-500">{article.readTime}</span>
                </div>
                <h3 className="text-lg font-semibold text-foreground leading-tight group-hover:text-blue-600 transition-colors line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {article.description}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default EditorialSection;